import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, Receipt as TransactionIcon, CheckCircle, Error } from '@mui/icons-material';
import { AccountTransaction, AccountPosting } from '../types/account';
import { accountService } from '../services/api';
import PostingTable from '../components/PostingTable';
import { formatCurrency, formatDateTime } from '../utils/accountUtils';

const TransactionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<AccountTransaction | null>(null);
  const [postings, setPostings] = useState<AccountPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadTransactionDetails();
    }
  }, [id]);

  const loadTransactionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const txn = await accountService.getTransaction(id!);
      setTransaction(txn);
      const postingData = await accountService.getTransactionPostings(id!);
      setPostings(postingData);
    } catch (err: any) {
      console.error('Error loading transaction:', err);
      setError(err.response?.data?.message || 'Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !transaction) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Transaction not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/transactions')}>
          Back to Transactions
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" variant="body1" onClick={() => navigate('/dashboard')}>
          Dashboard
        </Link>
        <Link component="button" variant="body1" onClick={() => navigate('/transactions')}>
          Transactions
        </Link>
        <Typography color="text.primary">{transaction.transactionRef || transaction.transactionReference}</Typography>
      </Breadcrumbs>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/transactions')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">{transaction.transactionRef || transaction.transactionReference}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(transaction.transactionDate)}
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={transaction.isBalanced ? <CheckCircle /> : <Error />}
          label={transaction.isBalanced ? 'Balanced' : 'Unbalanced'}
          color={transaction.isBalanced ? 'success' : 'error'}
        />
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Description</Typography>
              <Typography variant="body1" gutterBottom>{transaction.description}</Typography>

              <Typography variant="subtitle2" color="text.secondary" mt={2}>Total Amount</Typography>
              <Typography variant="h5" color="primary">
                {formatCurrency(transaction.totalAmount, transaction.currency)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Typography variant="body1" gutterBottom>{transaction.status}</Typography>

              {transaction.transactionType && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" mt={2}>Transaction Type</Typography>
                  <Typography variant="body1" gutterBottom>{transaction.transactionType.replace(/_/g, ' ')}</Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" mt={2}>Currency</Typography>
              <Typography variant="body1" gutterBottom>{transaction.currency}</Typography>

              <Typography variant="subtitle2" color="text.secondary" mt={2}>Number of Postings</Typography>
              <Typography variant="body1" gutterBottom>{transaction.numberOfPostings}</Typography>

              {transaction.createdBy && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" mt={2}>Created By</Typography>
                  <Typography variant="body1" gutterBottom>{transaction.createdBy}</Typography>
                </>
              )}

              {transaction.voucherNumber && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" mt={2}>Related Voucher</Typography>
                  <Link component="button" variant="body2" onClick={() => navigate(`/voucher/${transaction.voucherNumber}`)}>
                    {transaction.voucherNumber}
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Posting Lines</Typography>
        <PostingTable postings={postings} showAccount={true} showTransaction={false} showValidation={true} />
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Technical Details</Typography>
        <Typography variant="caption" color="text.secondary">ID</Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{transaction.id}</Typography>
      </Paper>
    </Box>
  );
};

export default TransactionDetail;
